import { Component, inject, OnInit, OnDestroy, signal, computed, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { BreadcrumbService, ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';

import { InventorySessionProxyService } from '../../../proxies/inventory/inventory-session.proxy.service';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { BarcodeScannerService } from '../../../services/barcode-scanner.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { InventorySession, InventorySessionScan, InventorySessionStatus } from '../../../models/inventory-session/inventory-session';
import { LocationNode } from '../../../models/location';
import { CreateChildSessionDialogComponent } from '../create-child-session-dialog/create-child-session-dialog.component';
import _ from 'lodash';
import { RollProxyService } from '../../../proxies/production/roll.proxy';

interface LocationOption {
  label: string;
  value: string;
}

interface InventorySessionForm {
  locationCode: FormControl<string | null>;
  name: FormControl<string | null>;
}

@Component({
  selector: 'app-inventory-session-details',
  templateUrl: './inventory-session-details.component.html',
  styleUrls: ['./inventory-session-details.component.scss'],
  providers: [],
})
export class InventorySessionDetailsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly proxyService = inject(InventorySessionProxyService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly barcodeScannerService = inject(BarcodeScannerService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly destroy$ = new Subject<void>();

  // State
  isLoading = signal<number>(0);
  isNewRecord = signal(true);
  session = signal<InventorySession | null>(null);
  selectedChildId = signal<string | null>(null);
  scans = signal<InventorySessionScan[]>([]);
  warehouseOptions = signal<LocationOption[]>([]);
  scanMode = signal<'nexus' | 'edr'>('nexus');
  showClosedChildren = signal(false);

  // Scan column filters — persist across scans and refreshes because they are signals
  filterLotItem = signal('');
  filterLocation = signal('');
  filterInsideLocation = signal<boolean | null>(null);
  filterScannedBy = signal('');

  @ViewChild('childContextMenu') childContextMenu!: ContextMenu;
  childContextMenuItems: MenuItem[] = [];

  exportMenuItems: MenuItem[] = [{ label: 'Export Detailed', icon: 'pi pi-list', command: () => this.exportSession(true) }];

  // Scans pagination
  scansPage = 1;
  scansPageSize = 50;
  scansTotalRecords = 0;

  // Form for create mode
  form = new FormGroup<InventorySessionForm>({
    locationCode: new FormControl<string | null>(null, [Validators.required]),
    name: new FormControl<string | null>(null),
  });

  // Computed
  isOpen = computed(() => this.session()?.status === InventorySessionStatus.Open);

  selectedChild = computed(() => {
    const parent = this.session();
    const childId = this.selectedChildId();
    if (!parent?.children || !childId) return null;
    return parent.children.find((c) => c.id === childId) ?? null;
  });

  selectedChildIsOpen = computed(() => {
    const child = this.selectedChild();
    if (!child) return false;
    return child.status === InventorySessionStatus.Open;
  });

  statusLabel = computed(() => {
    const s = this.session();
    if (!s) return '';
    return s.status === InventorySessionStatus.Open ? 'Open' : 'Closed';
  });

  statusSeverity = computed((): 'success' | 'info' | 'warn' | 'secondary' | 'danger' | undefined => {
    const s = this.session();
    if (!s) return undefined;
    return s.status === InventorySessionStatus.Open ? 'info' : 'secondary';
  });

  canDeleteScans = computed(() => this.selectedChildIsOpen() && AuthService.hasRole(UserGroup.InventorySessionAdmin));

  locationDisplay = computed(() => {
    const s = this.session();
    if (!s?.location?.fullName?.en) return s?.locationCode || '';
    const lang = this.translateService.currentLang || 'en';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (s.location?.fullName as any)[lang] || s.location?.fullName.en || s.locationCode;
  });

  filteredChildren = computed(() => {
    const parent = this.session();
    if (!parent?.children) return [];
    const filtered = this.showClosedChildren()
      ? parent.children
      : parent.children.filter((c) => c.status !== InventorySessionStatus.Closed);
    return [...filtered].sort((a, b) => a.scannedCount - b.scannedCount);
  });

  closedChildrenCount = computed(() => {
    const parent = this.session();
    if (!parent?.children) return 0;
    return parent.children.filter((c) => c.status === InventorySessionStatus.Closed).length;
  });

  filteredScans = computed(() => {
    let result = this.scans();
    const lotItem = this.filterLotItem().trim().toLowerCase();
    const location = this.filterLocation().trim().toLowerCase();
    const insideLocation = this.filterInsideLocation();
    const scannedBy = this.filterScannedBy().trim().toLowerCase();

    if (lotItem) result = result.filter((s) => s.lotItemNumber.toLowerCase().includes(lotItem));
    if (location) result = result.filter((s) => s.locationCodeAtScan?.toLowerCase().includes(location));
    if (insideLocation !== null) result = result.filter((s) => s.insideLocation === insideLocation);
    if (scannedBy) result = result.filter((s) => s.scannedByUserName?.toLowerCase().includes(scannedBy));

    return result;
  });

  get insideLocationFilterOptions(): { label: string; value: boolean }[] {
    return [
      { label: this.translateService.instant('Common_Yes') || 'Yes', value: true },
      { label: this.translateService.instant('Common_No') || 'No', value: false },
    ];
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params['id'];
      const childId = params['child'];

      if (!id || id === 'new') {
        this.isNewRecord.set(true);
        this.breadcrumbService.setCurrent('Common_New');
        this.loadLocations();
      } else {
        this.isNewRecord.set(false);
        this.loadSession(id);

        if (childId) {
          this.selectedChildId.set(childId);
          this.loadScans(childId);
        } else {
          this.selectedChildId.set(null);
          this.scans.set([]);
          this.scansTotalRecords = 0;
        }

        this.subscribeToHardwareScanner();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private loadLocations(): void {
    this.startLoading();
    this.locationProxyService
      .getAll()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          const locations = result?.data || [];
          // Filter to warehouses only (type === 1 is Warehouse in LocationType enum)
          const warehouses = locations.filter((loc: LocationNode) => loc.type === 1);
          this.warehouseOptions.set(
            warehouses.map((loc: LocationNode) => {
              const lang = this.translateService.currentLang || 'en';
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const label = (loc.name as any)?.[lang] || loc.name?.en || loc.code;
              return { label, value: loc.locationCode };
            }),
          );
        },
      });
  }

  private loadSession(id: string): void {
    this.startLoading();
    this.proxyService
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.session.set(result.data);
            this.breadcrumbService.setCurrent(result.data.name || result.data.number);
          }
        },
      });
  }

  loadScans(sessionId?: string): void {
    const id = sessionId || this.selectedChildId() || this.session()?.id;
    if (!id) return;

    this.startLoading();
    this.proxyService
      .getScans(id, this.scansPage, this.scansPageSize)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          this.scans.set(result?.data?.items || []);
          this.scansTotalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.scans.set([]);
          this.scansTotalRecords = 0;
        },
      });
  }

  private subscribeToHardwareScanner(): void {
    this.barcodeScannerService.cleanBarcode$.pipe(takeUntil(this.destroy$)).subscribe((code) => {
      // Skip if user is focused on an input field (manual search handles it)
      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') {
        return;
      }
      if (code && this.selectedChildIsOpen()) {
        this.handleScan(code);
      }
    });
  }

  createSession(): void {
    if (!this.form.valid) return;

    const locationCode = this.form.controls.locationCode.value!;
    const name = this.form.controls.name.value || undefined;

    this.startLoading();
    this.proxyService
      .create({ locationCode, name })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          if (result?.data?.id) {
            this.router.navigate(['/inventory-session-details'], {
              queryParams: { id: result.data.id },
            });
          }
        },
      });
  }

  selectChild(childId: string): void {
    const sessionId = this.session()?.id;
    if (!sessionId) return;
    this.scansPage = 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id: sessionId, child: childId },
    });
  }

  openCreateChildDialog(): void {
    const parent = this.session();
    if (!parent) return;

    this.modalService
      .open(
        CreateChildSessionDialogComponent,
        { modalClass: 'modal_small' },
        {
          parentId: parent.id,
          parentLocationCode: parent.locationCode,
        },
      )
      .subscribe((result) => {
        if (result.action === 'save' && result.data) {
          this.loadSession(parent.id);
        }
      });
  }

  // Manual search input
  lotItemSearchQuery = '';

  onQrCodeScanned(code: string): void {
    if (!this.selectedChildIsOpen()) return;
    this.handleScan(code);
  }

  onManualSearch(): void {
    const raw = this.lotItemSearchQuery.trim();
    if (!raw || !this.selectedChildIsOpen()) return;

    // Clean QR code: extract content after the last # (there can be ### in the qr code)
    let query = raw;
    if (raw.includes('#')) {
      query = raw.split('#').pop() || '';
    }

    if (query) {
      this.handleScan(query);
      this.lotItemSearchQuery = '';
    }
  }

  private attemptToGetByEdr(edrNumber: string): void {
    this.startLoading();
    this.rollProxyService
      .getByEdrNumber(edrNumber)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          if (result?.data?.number) {
            GlobalMessageUtil.info('Roll found for EDR', `Nexus Roll Number: ${result.data.number} for EDR number ${edrNumber}.`);
            this.submitScan(result.data.number);
          }
        },
      });
  }

  private handleScan(code: string): void {
    if (this.scanMode() === 'edr') {
      this.attemptToGetByEdr(code);
    } else {
      this.submitScan(code);
    }
  }

  private submitScan(code: string): void {
    const childId = this.selectedChildId();
    if (!childId || !code) return;

    this.proxyService.scan(childId, code).subscribe({
      next: (result) => {
        if (!_.isNil(result?.data)) {
          GlobalMessageUtil.success('Scanned', code, 2000);
          // Prepend new scan to the list
          this.scans.update((current) => [result.data!, ...current]);
          this.scansTotalRecords++;
          // Update child scanned count in parent's children array
          this.session.update((s) => {
            if (!s?.children) return s;
            return {
              ...s,
              totalChildScannedCount: s.totalChildScannedCount + 1,
              children: s.children.map((c) => (c.id === childId ? { ...c, scannedCount: c.scannedCount + 1 } : c)),
            };
          });
        }
      },
    });
  }

  deleteScan(scan: InventorySessionScan): void {
    const parentId = this.session()?.id;
    const childId = this.selectedChildId();
    if (!parentId || !childId) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete the scan for lot item "${scan.lotItemNumber}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: { label: 'Cancel', severity: 'secondary', outlined: true },
      acceptButtonProps: { label: 'Delete', severity: 'danger' },
      accept: () => {
        this.startLoading();
        this.proxyService
          .deleteScan(parentId, childId, scan.lotItemNumber)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: () => {
              GlobalMessageUtil.success('Deleted', `Scan for ${scan.lotItemNumber} deleted successfully.`);
              this.scans.update((current) => current.filter((s) => s.id !== scan.id));
              this.scansTotalRecords--;
              this.session.update((s) => {
                if (!s?.children) return s;
                return {
                  ...s,
                  totalChildScannedCount: s.totalChildScannedCount - 1,
                  children: s.children.map((c) => (c.id === childId ? { ...c, scannedCount: c.scannedCount - 1 } : c)),
                };
              });
            },
          });
      },
    });
  }

  closeSession(): void {
    const sessionId = this.session()?.id;
    if (!sessionId) return;

    this.confirmationService.confirm({
      message: this.translateService.instant('InventorySession_CloseParentConfirmMessage'),
      header: this.translateService.instant('InventorySession_CloseParentConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: { label: this.translateService.instant('Common_Cancel'), severity: 'secondary', outlined: true },
      acceptButtonProps: { label: this.translateService.instant('InventorySession_CloseParentConfirmAccept'), severity: 'warn' },
      accept: () => {
        this.startLoading();
        this.proxyService
          .updateStatus(sessionId, InventorySessionStatus.Closed)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (result) => {
              if (result?.data) {
                this.session.set(result.data);
                GlobalMessageUtil.success('Session Closed', 'The inventory session has been closed.');
              }
            },
          });
      },
    });
  }

  onChildContextMenu(event: MouseEvent, child: InventorySession): void {
    event.preventDefault();

    this.childContextMenuItems = [];

    if (child.status === InventorySessionStatus.Open) {
      this.childContextMenuItems.push({
        label: 'Close Child Session',
        icon: 'pi pi-lock',
        command: () => this.toggleChildStatus(child),
      });
    } else {
      this.childContextMenuItems.push({
        label: 'Reopen Child Session',
        icon: 'pi pi-lock-open',
        command: () => this.toggleChildStatus(child),
      });
    }

    this.childContextMenu.show(event);
  }

  toggleChildStatus(child: InventorySession, event?: Event): void {
    event?.stopPropagation();
    if (child.status === InventorySessionStatus.Open) {
      this.closeChildSession(child);
    } else {
      this.reopenChildSession(child);
    }
  }

  private closeChildSession(child: InventorySession): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to close child session "${child.locationCode}"?`,
      header: 'Confirm Close',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: { label: 'Cancel', severity: 'secondary', outlined: true },
      acceptButtonProps: { label: 'Close', severity: 'warn' },
      accept: () => {
        this.startLoading();
        this.proxyService
          .updateStatus(child.id, InventorySessionStatus.Closed)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: () => {
              GlobalMessageUtil.success('Session Closed', `Child session "${child.locationCode}" has been closed.`);
              this.session.update((s) => {
                if (!s?.children) return s;
                return {
                  ...s,
                  children: s.children.map((c) => (c.id === child.id ? { ...c, status: InventorySessionStatus.Closed } : c)),
                };
              });
            },
          });
      },
    });
  }

  private reopenChildSession(child: InventorySession): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to reopen child session "${child.locationCode}"?`,
      header: 'Confirm Reopen',
      icon: 'pi pi-question-circle',
      rejectButtonProps: { label: 'Cancel', severity: 'secondary', outlined: true },
      acceptButtonProps: { label: 'Reopen', severity: 'info' },
      accept: () => {
        this.startLoading();
        this.proxyService
          .updateStatus(child.id, InventorySessionStatus.Open)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: () => {
              GlobalMessageUtil.success('Session Reopened', `Child session "${child.locationCode}" has been reopened.`);
              this.session.update((s) => {
                if (!s?.children) return s;
                return {
                  ...s,
                  children: s.children.map((c) => (c.id === child.id ? { ...c, status: InventorySessionStatus.Open } : c)),
                };
              });
            },
          });
      },
    });
  }

  exportSession(detailed: boolean): void {
    const session = this.session();
    if (!session) return;

    this.startLoading();
    this.proxyService
      .export(session.id, detailed)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (blob) => {
          const fileName = `InventorySession_${session.name || session.number}${detailed ? '_detailed' : ''}.xlsx`;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
      });
  }

  onScansPageChange(event: PaginatorState): void {
    this.scansPage = (event.page || 0) + 1;
    this.scansPageSize = event.rows || this.scansPageSize;
    this.loadScans();
  }

  navigateBack(): void {
    this.router.navigate(['/inventory-sessions']);
  }
}
